import React from 'react';
import { StickyNote } from '../../types';
import MobileNoteCard from './MobileNoteCard';
import TreeConnector from './TreeConnector';
import './MobileTreeView.css';

interface MobileTreeViewProps {
  notes: StickyNote[];
  onNoteClick: (note: StickyNote) => void;
  onNoteDoubleClick: (note: StickyNote) => void;
  onDelete: (note: StickyNote) => void;
  onColorChange: (note: StickyNote, color: string) => void;
}

interface TreeNode {
  note: StickyNote;
  children: TreeNode[];
}

const MobileTreeView: React.FC<MobileTreeViewProps> = ({ 
  notes, 
  onNoteClick, 
  onNoteDoubleClick,
  onDelete,
  onColorChange
}) => {
  // Check if any note is currently creating a branch
  const isAnyNoteCreatingBranch = notes.some(note => note.isCreatingBranch);
  // Build tree structure from flat notes array
  const buildTree = (parentId: string | null): TreeNode[] => {
    const children = notes.filter(note => note.parentId === parentId);
    return children.map(note => ({
      note,
      children: buildTree(note.id)
    }));
  };

  // Calculate dynamic height for vertical connectors
  const calculateVerticalLineHeight = (children: TreeNode[]): string => {
    if (children.length === 0) return '0rem';
    
    // Calculate based on actual CSS values
    const notePadding = 1; // padding: 1rem (top + bottom = 2rem total)
    const noteContentHeight = 2.5; // Approximate content height (status badge + title + subtitle + content)
    const noteTotalHeight = notePadding * 2 + noteContentHeight; // Total note height
    const noteSpacing = 1.5; // margin-bottom: 1.5rem between branch notes
    const childrenContainerMargin = 1; // margin-top: 1rem of children container
    
    // Calculate total height needed
    // Start with children container margin
    let totalHeight = childrenContainerMargin;
    
    // Add height for each child note
    for (let i = 0; i < children.length; i++) {
      totalHeight += noteTotalHeight;
      
      // Add spacing after each note except the last one
      if (i < children.length - 1) {
        totalHeight += noteSpacing;
      }
    }
    
    // Add extra to reach the vertical center of the last note
    const extraHeight = noteTotalHeight / 2; // Half of note height to reach center
    
    return `${totalHeight + extraHeight}rem`;
  };

  // Get root notes (notes without parentId)
  const rootNotes = notes.filter(note => !note.parentId);
  
  // If no root notes, return empty state
  if (rootNotes.length === 0) {
    return (
      <div className={`mobile-tree-view ${isAnyNoteCreatingBranch ? 'branch-creating' : ''}`}>
        <div className="mobile-empty-state">
          <div className="mobile-empty-icon">💭</div>
          <h3 className="mobile-empty-title">No notes yet</h3>
         
        </div>
      </div>
    );
  }

  // Render tree node recursively
  const renderTreeNode = (node: TreeNode, depth: number = 0): React.ReactNode => {
    const { note, children } = node;
    const isRoot = depth === 0;
    
    return (
      <div key={note.id} className={`mobile-tree-node ${isRoot ? 'root' : 'branch'}`}>
        {/* Root note */}
        {isRoot && (
          <div className="mobile-root-container">
            <MobileNoteCard
              note={note}
              onClick={() => onNoteClick(note)}
              onDoubleClick={() => onNoteDoubleClick(note)}
              onDelete={() => onDelete(note)}
              onColorChange={(color) => onColorChange(note, color)}
              isRoot={true}
            />
            {children.length > 0 && (
              <TreeConnector 
                type="root-to-children" 
                height={calculateVerticalLineHeight(children)}
              />
            )}
          </div>
        )}
        
        {/* Branch notes */}
        {!isRoot && (
          <div className="mobile-branch-container">
            <TreeConnector type="branch-connector" />
            <MobileNoteCard
              note={note}
              onClick={() => onNoteClick(note)}
              onDoubleClick={() => onNoteDoubleClick(note)}
              onDelete={() => onDelete(note)}
              onColorChange={(color) => onColorChange(note, color)}
              isRoot={false}
            />
            {children.length > 0 && (
              <TreeConnector 
                type="branch-to-children" 
                height={calculateVerticalLineHeight(children)}
              />
            )}
          </div>
        )}
        
        {/* Render children */}
        {children.length > 0 && (
          <div className="mobile-children-container">
            {children.map(child => renderTreeNode(child, depth + 1))}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className={`mobile-tree-view ${isAnyNoteCreatingBranch ? 'branch-creating' : ''}`}>
      <div className="mobile-tree-container">
        {rootNotes.map(rootNote => {
          const rootNode: TreeNode = {
            note: rootNote,
            children: buildTree(rootNote.id)
          };
          return renderTreeNode(rootNode, 0);
        })}
      </div>
    </div>
  );
};

export default MobileTreeView;
